import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ModuleCardProps {
  id: string;
  title: string;
  name: string;
  description: string;
  isSelected: boolean;
  onToggle: (moduleId: string, isSelected: boolean) => void;
  isProcessing?: boolean;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ 
  id, 
  title,
  name,
  description,
  isSelected,
  onToggle,
  isProcessing = false
}) => {
  const navigate = useNavigate();
  const { packageId } = useParams();
  
  const handleToggle = () => {
    // Toggle: if selected, remove; if not selected, add
    onToggle(id, !isSelected);
  };
  
  return (
    <Card 
      className={cn(
        "p-4 transition-all duration-200 border shadow-sm hover:shadow-md",
        isSelected
          ? "border-blue-500 bg-blue-50"
          : "border-neutral-200 hover:border-neutral-300"
      )}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-neutral-900 mb-1">{title}</h3>
          <p className="text-sm text-neutral-500">{name}</p>
        </div>
        <div className="flex space-x-2 ml-4">
          <Button
            variant={isSelected ? "destructive" : "default"}
            size="sm"
            onClick={handleToggle}
            disabled={isProcessing}
            className={cn(
              "min-w-[120px]",
              isSelected 
                ? "bg-red-600 hover:bg-red-700 text-white" 
                : "bg-blue-600 hover:bg-blue-700 text-white"
            )}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isSelected ? "Removendo..." : "Adicionando..."}
              </>
            ) : (
              isSelected ? "Remover" : "Adicionar"
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-edit
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/packages/${packageId}/modules/${id}`);
            }}
          >
            Edit
          </Button>
        </div>
      </div>
      <p className="text-neutral-600 text-sm">{description}</p>
    </Card>
  );
};

export default ModuleCard;